import {
  Button,
  CurrencyInput,
  DatePicker,
  Input,
  Modal,
  PasteButton,
  SelectAllToggle,
  Textarea,
  formatCurrency,
  isCivilDate,
  type CivilDate,
  IconButton,
} from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { Barcode, Check, Copy, FileUp, PencilLine, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  entriesApi,
  getApiError,
  type Entry,
  type PaymentSlip,
  type SuggestedSlip,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { RequestAlert } from '../../app/data-view.js';
import { decodeSlipLine, digitsOnly, formatSlipLine } from './slip-line.js';

/// Boletos de uma compra em carnê: um por parcela, com a linha digitável pronta para copiar.
///
/// Vencimento e valor saem da própria linha e aparecem preenchidos para CONFERÊNCIA — o operador
/// pode corrigi-los antes de salvar, porque carnê renegociado tem linha que não bate mais com o
/// combinado. Nada é gravado sem passar pela validação do servidor.
export function EntrySlipsDialog({
  entry,
  today,
  timeZone,
  canManage,
  onClose,
}: {
  entry: Entry;
  today: CivilDate;
  timeZone: string;
  canManage: boolean;
  onClose: () => void;
}) {
  const [editing, setEditing] = useState<PaymentSlip>();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<PaymentSlip>();
  const [found, setFound] = useState<SuggestedSlip[]>();

  const slips = useQuery({
    queryKey: ['company', 'slips', entry.id],
    queryFn: () => entriesApi.slips(entry.id),
    retry: false,
  });

  const list = slips.data ?? [];
  const total = list.reduce((sum, slip) => sum + slip.amountCents, 0);
  // A próxima parcela livre é a sugestão óbvia: cadastra-se carnê na ordem em que ele é impresso.
  const nextInstallment =
    list.reduce((highest, slip) => Math.max(highest, slip.installmentNumber), 0) + 1;

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Boletos"
      description={`${entry.description} · a linha digitável de cada parcela.`}
    >
      {canManage && !adding && !editing && !found ? (
        <div className="mb-5 flex flex-col gap-3 sm:flex-row">
          <CarnePicker entryId={entry.id} onRead={setFound} />
          <Button
            className="w-full sm:w-auto"
            type="button"
            variant="outline"
            onClick={() => setAdding(true)}
          >
            <Plus className="size-4" />
            Digitar boleto
          </Button>
        </div>
      ) : null}

      {found ? (
        <CarneReview
          entryId={entry.id}
          found={found}
          timeZone={timeZone}
          onCancel={() => setFound(undefined)}
          onSaved={async () => {
            await slips.refetch();
            setFound(undefined);
          }}
        />
      ) : null}

      {adding || editing ? (
        <SlipForm
          entryId={entry.id}
          today={today}
          {...(editing ? { slip: editing } : { suggestedInstallment: nextInstallment })}
          onCancel={() => {
            setAdding(false);
            setEditing(undefined);
          }}
          onSaved={async () => {
            await slips.refetch();
            setAdding(false);
            setEditing(undefined);
          }}
        />
      ) : null}

      {slips.isLoading ? (
        <p className="text-sm text-content-muted">Carregando boletos…</p>
      ) : slips.error ? (
        <RequestAlert message={getApiError(slips.error)} />
      ) : list.length === 0 ? (
        <p className="rounded-xl border border-surface-border bg-surface-elevated px-4 py-6 text-center text-sm text-content-muted">
          Nenhum boleto cadastrado nesta compra.
        </p>
      ) : (
        <>
          <ul
            aria-label="Boletos cadastrados"
            className="divide-y divide-surface-border rounded-xl border border-surface-border"
          >
            {list.map((slip) => (
              <li key={slip.id} className="px-4 py-3">
                <div className="flex flex-wrap items-start gap-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-500/10 text-xs font-semibold text-brand-300">
                    {slip.installmentNumber}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="break-all font-mono text-sm text-content">
                      {formatSlipLine(slip.line)}
                    </p>
                    <p className="mt-1 text-xs text-content-faint">
                      {formatCurrency(slip.amountCents)} ·{' '}
                      {slip.dueOn
                        ? `vence em ${formatDate(`${slip.dueOn}T12:00:00Z`, timeZone)}`
                        : 'sem vencimento na linha'}
                      {slip.origin === 'PDF' ? ' · lido do carnê' : ''}
                    </p>
                    {slip.description ? (
                      <p className="mt-1 text-xs text-content-muted">{slip.description}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1">
                    <CopyLineButton line={slip.line} installmentNumber={slip.installmentNumber} />
                    {canManage ? (
                      <>
                        <IconButton
                          label="Corrigir"
                          icon={<PencilLine />}
                          onClick={() => {
                            setAdding(false);
                            setEditing(slip);
                          }}
                        />
                        <IconButton
                          label="Remover"
                          icon={<Trash2 />}
                          tone="danger"
                          onClick={() => setRemoving(slip)}
                        />
                      </>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-content-muted">
            {list.length} {list.length === 1 ? 'boleto' : 'boletos'} · {formatCurrency(total)} no
            total
            {/* O carnê pode não cobrir a compra inteira: juros, entrada à vista, parcela paga fora
                dele. Apontar a diferença é melhor do que fingir que os números têm de bater. */}
            {total !== entry.amountCents
              ? ` · a compra é de ${formatCurrency(entry.amountCents)}`
              : ''}
          </p>
        </>
      )}

      <div className="mt-7 flex justify-end border-t border-surface-border pt-5">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Concluir
        </Button>
      </div>

      {removing ? (
        <RemoveSlipDialog
          slip={removing}
          onClose={() => setRemoving(undefined)}
          onRemoved={async () => {
            await slips.refetch();
            setRemoving(undefined);
          }}
        />
      ) : null}
    </Modal>
  );
}

/// Escolher o carnê em PDF. A leitura não grava nada: o que volta vai para a conferência.
function CarnePicker({
  entryId,
  onRead,
}: {
  entryId: string;
  onRead: (slips: SuggestedSlip[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function read(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setError(undefined);
    setBusy(true);
    try {
      const slips = await entriesApi.readCarne(entryId, file);
      if (slips.length === 0) {
        setError('Nenhum boleto foi encontrado neste arquivo. Digite o boleto à mão.');
        return;
      }
      onRead(slips);
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="min-w-0">
      <Button
        className="w-full sm:w-auto"
        type="button"
        loading={busy}
        onClick={() => inputRef.current?.click()}
      >
        <FileUp className="size-4" />
        Ler carnê em PDF
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="sr-only"
        aria-label="Arquivo do carnê"
        onChange={(event) => void read(event.target.files)}
      />
      {error ? <RequestAlert message={error} /> : null}
    </div>
  );
}

/// A conferência antes de gravar. O que a leitura achou aparece com vencimento e valor tirados das
/// próprias linhas; o operador desmarca o que não quer e só então salva. O que já está cadastrado
/// vem desmarcado e travado — cadastrar de novo daria conflito de parcela.
function CarneReview({
  entryId,
  found,
  timeZone,
  onCancel,
  onSaved,
}: {
  entryId: string;
  found: SuggestedSlip[];
  timeZone: string;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [chosen, setChosen] = useState<Set<string>>(
    () => new Set(found.filter((slip) => !slip.alreadySaved).map((slip) => slip.line)),
  );
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const selected = found.filter((slip) => chosen.has(slip.line));
  const selectable = found.filter((slip) => !slip.alreadySaved);

  async function save() {
    setError(undefined);
    setSaving(true);
    try {
      await entriesApi.saveSlips(
        entryId,
        selected.map((slip) => ({
          installmentNumber: slip.installmentNumber,
          line: slip.line,
          dueOn: slip.dueOn,
          amountCents: slip.amountCents,
          description: null,
          origin: 'PDF' as const,
        })),
      );
      await onSaved();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-5 rounded-xl border border-surface-border bg-surface-elevated p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-content">
          {found.length} {found.length === 1 ? 'boleto encontrado' : 'boletos encontrados'}
        </p>
        <SelectAllToggle
          scope="boletos encontrados"
          total={selectable.length}
          selected={selectable.filter((slip) => chosen.has(slip.line)).length}
          onSelectAll={() => setChosen(new Set(selectable.map((slip) => slip.line)))}
          onClear={() => setChosen(new Set())}
        />
      </div>
      <p className="mt-1 text-xs text-content-muted">
        Vencimento e valor saem da própria linha digitável. Confira antes de salvar.
      </p>

      <ul aria-label="Boletos encontrados no carnê" className="mt-4 grid gap-2">
        {found.map((slip) => (
          <li key={slip.line}>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-surface-border bg-surface-card px-3 py-2">
              <input
                type="checkbox"
                className="mt-1 size-4 accent-brand-500"
                checked={chosen.has(slip.line)}
                disabled={slip.alreadySaved}
                onChange={(event) =>
                  setChosen((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(slip.line);
                    else next.delete(slip.line);
                    return next;
                  })
                }
              />
              <span className="min-w-0 flex-1">
                <span className="block break-all font-mono text-xs text-content">
                  {formatSlipLine(slip.line)}
                </span>
                <span className="mt-1 block text-xs text-content-faint">
                  Parcela {slip.installmentNumber} · {formatCurrency(slip.amountCents)} ·{' '}
                  {slip.dueOn
                    ? `vence em ${formatDate(`${slip.dueOn}T12:00:00Z`, timeZone)}`
                    : 'sem vencimento na linha'}
                  {slip.alreadySaved ? ' · já cadastrado' : ''}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>

      {error ? <RequestAlert message={error} /> : null}

      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          className="w-full sm:w-auto"
          type="button"
          loading={saving}
          disabled={selected.length === 0}
          onClick={() => void save()}
        >
          {selected.length === 1 ? 'Salvar 1 boleto' : `Salvar ${selected.length} boletos`}
        </Button>
      </div>
    </div>
  );
}

/// Copiar é a razão de o boleto estar aqui: o operador cola a linha no app do banco.
function CopyLineButton({ line, installmentNumber }: { line: string; installmentNumber: number }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(line);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Área de transferência negada pelo navegador: a linha continua à vista para copiar à mão.
      setCopied(false);
    }
  }

  return (
    <IconButton
      label={
        copied
          ? `Linha digitável da parcela ${installmentNumber} copiada`
          : `Copiar a linha digitável da parcela ${installmentNumber}`
      }
      icon={copied ? <Check /> : <Copy />}
      onClick={() => void copy()}
    />
  );
}

interface SlipFormState {
  installmentNumber: string;
  line: string;
  dueOn: CivilDate | null;
  amountCents: number | null;
  description: string;
}

function SlipForm({
  entryId,
  slip,
  suggestedInstallment,
  today,
  onCancel,
  onSaved,
}: {
  entryId: string;
  slip?: PaymentSlip | undefined;
  suggestedInstallment?: number | undefined;
  today: CivilDate;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState<SlipFormState>(() => ({
    installmentNumber: String(slip?.installmentNumber ?? suggestedInstallment ?? 1),
    line: slip ? formatSlipLine(slip.line) : '',
    dueOn: slip?.dueOn && isCivilDate(slip.dueOn) ? slip.dueOn : null,
    amountCents: slip?.amountCents ?? null,
    description: slip?.description ?? '',
  }));
  const [error, setError] = useState<string>();
  const [pasteError, setPasteError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const typed = digitsOnly(form.line);
  const decoded = decodeSlipLine(form.line);
  // Enquanto não há 47 dígitos, o número está sendo digitado — cobrar validade seria implicância.
  const lineError = typed.length === 47 && !decoded ? 'Esta linha digitável não confere.' : '';

  /// Ao reconhecer a linha, vencimento e valor dela entram no formulário. Só onde o operador ainda
  /// não escreveu: o que ele digitou é decisão, não rascunho a ser sobrescrito.
  function changeLine(value: string) {
    const read = decodeSlipLine(value);
    setForm((current) => ({
      ...current,
      line: value,
      dueOn:
        current.dueOn === null && read?.dueOn && isCivilDate(read.dueOn)
          ? read.dueOn
          : current.dueOn,
      amountCents: current.amountCents === null ? (read?.amountCents ?? null) : current.amountCents,
    }));
  }

  async function save() {
    setError(undefined);
    setSaving(true);
    try {
      const input = {
        installmentNumber: Number(form.installmentNumber || '0'),
        line: digitsOnly(form.line),
        dueOn: form.dueOn,
        amountCents: form.amountCents,
        description: form.description.trim() || null,
      };
      if (slip) {
        await entriesApi.updateSlip(slip.id, { ...input, origin: slip.origin });
      } else {
        await entriesApi.saveSlips(entryId, [input]);
      }
      await onSaved();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      noValidate
      aria-label={slip ? 'Corrigir boleto' : 'Novo boleto'}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
      className="mb-5 rounded-xl border border-surface-border bg-surface-elevated p-4"
    >
      <div className="grid gap-5">
        <Input
          label="Linha digitável"
          autoComplete="off"
          inputMode="numeric"
          className="font-mono"
          hint="Os 47 dígitos do boleto. Cole com ou sem os pontos."
          value={form.line}
          error={lineError || pasteError}
          onChange={(event) => changeLine(event.target.value)}
          action={
            <PasteButton
              onPaste={(text) => {
                setPasteError(undefined);
                changeLine(text);
              }}
              onError={setPasteError}
            />
          }
        />
        {decoded ? (
          <p className="-mt-3 text-xs text-content-muted">
            A linha diz{' '}
            <strong className="text-content">{formatCurrency(decoded.amountCents)}</strong>
            {decoded.dueOn ? (
              <>
                {' '}
                com vencimento em{' '}
                <strong className="text-content">
                  {decoded.dueOn.split('-').reverse().join('/')}
                </strong>
              </>
            ) : (
              ' e não traz vencimento'
            )}
            . Confira antes de salvar.
          </p>
        ) : null}

        <div className="grid gap-5 sm:grid-cols-3">
          <Input
            label="Parcela"
            inputMode="numeric"
            autoComplete="off"
            value={form.installmentNumber}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                installmentNumber: event.target.value.replace(/\D/gu, ''),
              }))
            }
          />
          <DatePicker
            label="Vencimento"
            value={form.dueOn}
            onValueChange={(value) => setForm((current) => ({ ...current, dueOn: value }))}
            today={today}
          />
          <CurrencyInput
            label="Valor"
            value={form.amountCents}
            onValueChange={(cents) => setForm((current) => ({ ...current, amountCents: cents }))}
          />
        </div>

        <Textarea
          label="Observação"
          rows={2}
          value={form.description}
          onChange={(event) =>
            setForm((current) => ({ ...current, description: event.target.value }))
          }
        />
      </div>

      {error ? <RequestAlert message={error} /> : null}

      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button className="w-full sm:w-auto" loading={saving}>
          {slip ? 'Salvar boleto' : 'Adicionar boleto'}
        </Button>
      </div>
    </form>
  );
}

function RemoveSlipDialog({
  slip,
  onClose,
  onRemoved,
}: {
  slip: PaymentSlip;
  onClose: () => void;
  onRemoved: () => Promise<void>;
}) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setError(undefined);
    setBusy(true);
    try {
      await entriesApi.removeSlip(slip.id);
      await onRemoved();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Remover boleto"
      description="A parcela fica sem linha digitável."
    >
      <p className="text-sm leading-6 text-content-secondary">
        Remover o boleto da parcela <strong>{slip.installmentNumber}</strong>? A compra e o carnê
        anexado continuam onde estão; só a linha digitável some.
      </p>
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="w-full sm:w-auto"
          type="button"
          loading={busy}
          onClick={() => void confirm()}
        >
          Remover boleto
        </Button>
      </div>
    </Modal>
  );
}

export { Barcode as SlipIcon };
